/**
 * MCP Connection API service — ออก/ดู/ถอน personal access token ของ remote MCP.
 *
 * ห่อ 3 endpoint ใต้ `api/v1/McpToken` (McpTokenController). โครง axios / base-URL /
 * cookie-auth / unwrap คัดลอกมาจาก `services/taxLogV2Api.js` ตรง ๆ (house style ของตระกูล V2)
 * เพื่อให้ diff กันได้ — ต่างกันแค่ `ROOT` กับรายชื่อเมธอด
 *
 * ── 🔴 ค่าโทเคนเต็มปรากฏครั้งเดียว
 * `issue()` คืน `{ token: 'fcb_…' }` — **เก็บไว้แสดงในหน้าจอเท่านั้น** ห้าม `localStorage`,
 * ห้ามใส่ query string, ห้ามส่งเข้า log/analytics ใด ๆ · backend เก็บแต่ SHA-256
 * ปิดหน้าจอแล้วอ่านซ้ำไม่ได้อีก และไม่มี endpoint ไหนคืนค่านี้ให้อีกเลย
 * `list()` คืนได้แค่ 4 ตัวท้าย (`tokenSuffix`) ไว้ให้คนจับคู่ใบที่ถืออยู่
 *
 * ── สิทธิ์
 * เฉพาะ admin (role 0/1) — ด่านจริงอยู่ที่ `APIAuthen` (region McpTokenController)
 * ⇒ ผู้ใช้ทั่วไปที่หลุดเข้ามาจะได้ 401 ตั้งแต่ชั้น cookie ไม่ใช่ error จากหน้าจอ
 *
 * response ทุกตัวเป็น ResponseModel `{ Message, Status, Data }` มาตรฐาน — helper ด้านล่าง
 * แกะ `.Data` และ throw เมื่อ `Status` ไม่ใช่ 0 (APIStatus.Successful)
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// instance ของตัวเอง · ตั้ง withCredentials ตรง ๆ (cookie session auth)
// เพื่อไม่ให้ต้องพึ่งว่า global default ของ axios ถูกตั้งมาก่อนแล้ว
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/** แกะ ResponseModel `{ message, status, data }` → `data` · throw เมื่อ envelope บอกว่าไม่สำเร็จ */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** แปลง error ของ axios ให้เป็น Error ที่พก message ของ API มาด้วย */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/McpToken';

export const mcpTokenApi = {
  /**
   * ออกโทเคนใบใหม่ให้ผู้เรียกเอง
   *
   * 🔴 `Data.token` คือค่าเต็มที่ปรากฏ **ครั้งเดียว** — ดูข้อกำหนดที่หัวไฟล์
   *
   * @param {{ tokenName: string, allowWrite: boolean, expiresAt?: string|null }} payload
   *   `tokenName` บังคับใส่ (ชื่อคือสิ่งเดียวที่ไล่ได้ว่าจะถอนใบไหน) ·
   *   `allowWrite` false = อ่านอย่างเดียว · `expiresAt` null = ไม่หมดอายุ
   * @returns {Promise<Object>} { id, token, tokenName, tokenSuffix, allowWrite, createDate, ... }
   */
  issue(payload) {
    return post(`${ROOT}/issue`, payload);
  },

  /**
   * โทเคนทุกใบของผู้เรียก (รวมที่ถอนแล้ว) เรียงใหม่สุดขึ้นก่อน
   * — ไม่มี hash ไม่มีค่าเต็ม มีแค่ `tokenSuffix` 4 ตัวท้าย
   *
   * @returns {Promise<Array>} [{ id, tokenName, tokenSuffix, allowWrite, isUsable,
   *   createDate, lastUsedDate, revokedDate, revokedBy, expiresAt }]
   */
  list() {
    return get(`${ROOT}/list`);
  },

  /**
   * ถอนโทเคน — มีผลทันที (ชั้น runtime ตรวจทุกคำขอ ไม่มี cache)
   * ถอนได้เฉพาะใบของตัวเอง · ถอนซ้ำไม่ error
   *
   * @param {number} id
   */
  revoke(id) {
    return post(`${ROOT}/${id}/revoke`, {});
  },
};

export default mcpTokenApi;
